import {
  createContext,
  useContext,
  useEffect,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { StaffApi } from "#api.ts";
const AuthContext = createContext<StaffApi | undefined>(undefined);

/** Own the staff session, including renewal across tablet sleep/wake. */
export function AuthProvider({
  api,
  children,
}: {
  readonly api: StaffApi;
  readonly children: ReactNode;
}) {
  const state = useSyncExternalStore(api.subscribe, api.snapshot);
  useEffect(() => {
    void api.refresh();
  }, [api]);
  useEffect(() => {
    if (state.kind !== "authenticated") return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let shiftExpiryTimer: ReturnType<typeof setTimeout> | undefined;
    const renew = async () => {
      const result = await api.credential();
      if (!result.ok && !cancelled)
        timer = setTimeout(() => void renew(), 60_000);
    };
    timer = setTimeout(
      () => void renew(),
      Math.max(1_000, state.expiresAt - Date.now() - 60_000),
    );
    if (state.sessionKind === "kitchen-shift")
      shiftExpiryTimer = setTimeout(
        () => void api.refresh(),
        Math.max(1_000, state.shiftExpiresAt - Date.now()),
      );
    const onVisible = () => {
      if (document.visibilityState === "visible") void renew();
    };
    window.addEventListener("online", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (shiftExpiryTimer) clearTimeout(shiftExpiryTimer);
      window.removeEventListener("online", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, state]);
  return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>;
}
/** Read the reactive session and its shared request adapter. */
export function useAuth() {
  const api = useContext(AuthContext);
  if (!api) throw new Error("AuthProvider is missing.");
  const state = useSyncExternalStore(api.subscribe, api.snapshot);
  return {
    api,
    state,
    login: api.login,
    loginWithPasskey: api.loginWithPasskey,
    loginWithOneTimeToken: api.loginWithOneTimeToken,
    logout: () => api.logout(),
  };
}
